import React, { useState, lazy, Suspense, memo } from 'react';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import { Field } from 'formik';
import { toast } from 'react-toastify';
import { GoogleLogin } from '@react-oauth/google';
import * as msal from '@azure/msal-browser';
import AppConstants from '../../core-components/AppConstants';
import AppHelper from '../../core-components/AppHelper';
import styles from './Login.module.scss';
import { getErrorMessage } from '../../utils/utilities';

const LoginHeader = lazy(() => import('./LoginHeader'));
const ValidatedFormInputField = lazy(() =>
  import('../../core-components/ValidatedFormInputField/ValidatedFormInputField')
);
const Button = lazy(() => import('../../core-components/Button'));

const LoginForm = (props) => {
  const [enableGLogin, setEnableGLogin] = useState(true);
  const { handleSubmit, isSubmitting } = props;
  const microsoftEnvironmentConstant = require('../../config/microsoft-auth.json');
  const msalConfig = {
    auth: {
      clientId: microsoftEnvironmentConstant[AppHelper.getEnv()].clientId,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: `${AppConstants.baseURL}redirect`,
      cache: {
        cacheLocation: 'sessionStorage', // This configures where your cache will be stored
        storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
      }
    }
  };

  const loginRequest = {
    scopes: ['User.Read'],
    forceRefresh: true //Set this to "true" to skip a cached token and go to the server to get a new token
  };

  const msalInstance = new msal.PublicClientApplication(msalConfig);

  const handleMicrosoftLogin = async () => {
    msalInstance
      .loginPopup(loginRequest)
      .then((loginResponse) => {
        handleSuccess(null, loginResponse);
      })
      .catch((error) => {
        getErrorMessage(error);
      });
  };

  const handleSuccess = async (googleData, microsoftData = null) => {
    const url = microsoftData != null ? 'auth/microsoft-login' : 'auth/google-login';

    const res = await fetch(`${AppConstants.developmentAPIURL}${url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token: microsoftData != null ? microsoftData?.accessToken : googleData?.credential
      })
    });
    const data = await res.json();
    if (data.data) {
      props.googleMicrosoftLogin(data.data);
      props.history.push('/dashboard');
    } else {
      if (
        (data?.body && !data?.body?.login_method_google) ||
        (data?.body && !data?.body?.login_method_microsoft)
      ) {
        props.setFieldValue('loginError', data.message);
      } else {
        toast.error(data.message);
      }
    }
  };

  const handleFailure = (e) => {
    console.log(e.error);
  };

  return (
    <div className={styles.loginWrapper}>
      <Suspense fallback={<div>Loading...</div>}>
        <Container>
          <div className={styles.loginCard} data-testid='login-box'>
            <div className={styles.loginWelcomeNote}>
              <h1>Welcome!</h1>
              <h1>Login Here</h1>
            </div>
            {enableGLogin && (
              <div>
                <div className={styles.googleLoginContainer}>
                  <GoogleLogin
                    onSuccess={handleSuccess}
                    onError={(e) => {
                      handleFailure(e);
                    }}
                    useOneTap={true}
                    cancel_on_tap_outside={false}
                    theme='filled_blue'
                    width='750'
                  />
                </div>
                <div className={styles.microsoftLoginContainer} onClick={handleMicrosoftLogin}>
                  <div className={styles.microsoftLoginIcon}></div>
                  <div className={styles.microsoftLoginText}>Sign in with Microsoft</div>
                </div>
                <div className={styles.separator}>OR</div>
              </div>
            )}
            <Form onSubmit={handleSubmit}>
              <Field
                data-testid='email-field'
                component={ValidatedFormInputField}
                type='text'
                name='email'
                placeholder='Enter Email'
                label='Email ID'
              />
              <Field
                data-testid='password-field'
                component={ValidatedFormInputField}
                type='password'
                name='password'
                placeholder='Enter Password'
                label='Password'
              />
              <div>
                <Button
                  data-testid='forgot-password-button'
                  onClick={() => props.history.push('/forgot-password')}
                  className={styles.forgotPasswordBtn}
                >
                  Forgot password?
                </Button>
              </div>
              <div>
                <Button
                  type='submit'
                  disabled={isSubmitting || props.values.loginError}
                  className={styles.signInBtn}
                  data-testid='login-button'
                >
                  Sign In
                </Button>
              </div>
              {props.values.loginError && (
                <div className={styles.errorMsg}>{props.values.loginError}</div>
              )}
            </Form>
          </div>
        </Container>
      </Suspense>
    </div>
  );
};

export default memo(LoginForm);
